import React from 'react';
import {
    Box,
    IconButton,
    Tooltip,
    Divider,
    Typography
} from '@mui/material';
import {
    CreateNewFolder,
    FolderOpen,
    Save,
    PlayArrow,
    Stop,
    Brightness4,
    Brightness7
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from 'src/hooks/redux';
import { addNotification, setTheme } from 'src/store/slices/uiSlice';

interface ToolBarProps {
    onNewProject: () => void;
    onOpenProject: () => void;
    onSaveProject: () => void;
    onRunProject: () => void;
    onStopProject: () => void;
    currentTheme?: 'light' | 'dark';
}

const ToolBar: React.FC<ToolBarProps> = ({
    onNewProject,
    onOpenProject,
    onSaveProject,
    onRunProject,
    onStopProject,
    currentTheme = 'light'
}) => {
    const dispatch = useAppDispatch();
    const { currentProject } = useAppSelector(state => state.project);
    const { connected: rosConnected } = useAppSelector(state => state.ros as any);
    const { connected: gazeboConnected } = useAppSelector(state => state.gazebo as any);
    const { theme } = useAppSelector(state => state.ui);

    const handleSave = () => {
        if (currentProject) {
            onSaveProject();
            dispatch(addNotification({
                type: 'success',
                title: 'Project Saved',
                message: `${currentProject.name} saved successfully.`
            }));
        } else {
            dispatch(addNotification({
                type: 'warning',
                title: 'No Active Project',
                message: 'Please create or open a project first.'
            }));
        }
    };

    const handleRun = () => {
        if (!currentProject) {
            dispatch(addNotification({
                type: 'error',
                title: 'No Project',
                message: 'Please create or open a project before running.'
            }));
            return;
        }

        if (!rosConnected) {
            dispatch(addNotification({
                type: 'warning',
                title: 'ROS2 Not Connected',
                message: 'Please connect to ROS2 before running the project.'
            }));
            return;
        }

        onRunProject();
    };

    return (
        <Box
            data-tour="toolbar"
            sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: currentTheme === 'dark' ? 'grey.800' : 'grey.50',
                borderBottom: '1px solid',
                borderColor: currentTheme === 'dark' ? 'grey.600' : 'grey.200',
                px: 2,
                py: 0.5,
                minHeight: 40,
                gap: 2
            }}
        >
            {/* Essential Actions */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="New Project">
                    <IconButton
                        data-tour="new-project"
                        size="small"
                        onClick={onNewProject}
                        sx={{ padding: 0.5 }}
                    >
                        <CreateNewFolder fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Open Project">
                    <IconButton size="small" onClick={onOpenProject} sx={{ padding: 0.5 }}>
                        <FolderOpen fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Save Project">
                    <IconButton
                        data-tour="save"
                        size="small"
                        onClick={handleSave}
                        disabled={!currentProject}
                        sx={{ padding: 0.5 }}
                    >
                        <Save fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    height: 24,
                    borderColor: currentTheme === 'dark' ? 'grey.600' : 'grey.300'
                }}
            />

            {/* Run Controls */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Run Project">
                    <IconButton
                        size="small"
                        onClick={handleRun}
                        disabled={!currentProject || !rosConnected}
                        sx={{
                            padding: 0.5,
                            color: rosConnected && currentProject ? 'success.main' : 'text.disabled'
                        }}
                    >
                        <PlayArrow fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Stop Project">
                    <IconButton
                        size="small"
                        onClick={onStopProject}
                        disabled={!currentProject}
                        sx={{
                            padding: 0.5,
                            color: currentProject ? 'error.main' : 'text.disabled'
                        }}
                    >
                        <Stop fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    height: 24,
                    borderColor: currentTheme === 'dark' ? 'grey.600' : 'grey.300'
                }}
            />

            {/* Project Info - Minimalistic */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                {currentProject ? (
                    <>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {currentProject.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {currentProject.type.replace('_', ' ')}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        No project
                    </Typography>
                )}
            </Box>

            {/* Compact Status */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: rosConnected ? 'success.main' : 'error.main'
                        }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        ROS2
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: gazeboConnected ? 'success.main' : 'error.main'
                        }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        Gazebo
                    </Typography>
                </Box>
            </Box>

            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    height: 24,
                    borderColor: currentTheme === 'dark' ? 'grey.600' : 'grey.300'
                }}
            />

            {/* Theme Toggle */}
            <Tooltip title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                <IconButton
                    size="small"
                    onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
                    sx={{ padding: 0.5 }}
                >
                    {theme === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default ToolBar;